import { InternalVariable } from '@app/types/common';

/**
 * Weather condition types that can be derived from forecast data
 */
export type WeatherCondition =
  | 'clear'
  | 'mostly_clear'
  | 'partly_cloudy'
  | 'mostly_cloudy'
  | 'cloudy'
  | 'light_rain'
  | 'rain'
  | 'heavy_rain';

/**
 * Time of day classification for determining day/night icons
 */
export type TimeOfDay = 'day' | 'night';

/**
 * Complete weather state for a given time point
 */
export interface WeatherState {
  condition: WeatherCondition;
  timeOfDay: TimeOfDay;
  temperature: number;
  precipitation: number;
  cloudCover: number;
  humidity: number;
  windSpeed: number;
}

/**
 * Determines the time of day (day/night) based on hour
 * This is a simplified version - ideally would use sunrise/sunset calculations
 *
 * @param hour - Hour in 24-hour format (0-23)
 * @returns 'day' or 'night'
 */
export function determineTimeOfDay(hour: number): TimeOfDay {
  // Simple rule: day is from 6 AM to 8 PM
  if (hour >= 6 && hour < 20) {
    return 'day';
  }
  return 'night';
}

/**
 * Calculates the total cloud cover percentage from low, medium, and high cloud layers
 * Uses a weighted approach where lower clouds have more visual impact
 *
 * @param lowCloud - Low altitude cloud cover percentage (0-100)
 * @param mediumCloud - Medium altitude cloud cover percentage (0-100)
 * @param highCloud - High altitude cloud cover percentage (0-100)
 * @returns Total effective cloud cover percentage (0-100)
 */
export function calculateTotalCloudCover(
  lowCloud: number = 0,
  mediumCloud: number = 0,
  highCloud: number = 0,
): number {
  // Weight lower clouds more heavily as they're more visually prominent
  // Low clouds: 50%, Medium: 30%, High: 20%
  const weightedAverage = lowCloud * 0.5 + mediumCloud * 0.3 + highCloud * 0.2;

  // Also consider the maximum cloud cover to avoid underestimating overcast conditions
  const maxCover = Math.max(lowCloud, mediumCloud, highCloud);

  // Blend weighted average with max (70/30 split)
  return weightedAverage * 0.7 + maxCover * 0.3;
}

/**
 * Determines the weather condition based on precipitation and cloud cover
 *
 * @param precipitation - Precipitation amount in mm/hour
 * @param cloudCover - Total cloud cover percentage (0-100)
 * @returns The weather condition classification
 */
export function determineWeatherCondition(
  precipitation: number,
  cloudCover: number,
): WeatherCondition {
  // Precipitation takes priority over cloud conditions
  if (precipitation > 0.1) {
    if (precipitation < 2.5) {
      return 'light_rain';
    } else if (precipitation < 7.5) {
      return 'rain';
    } else {
      return 'heavy_rain';
    }
  }

  // No significant precipitation, use cloud cover to determine condition
  if (cloudCover < 10) {
    return 'clear';
  } else if (cloudCover < 30) {
    return 'mostly_clear';
  } else if (cloudCover < 60) {
    return 'partly_cloudy';
  } else if (cloudCover < 85) {
    return 'mostly_cloudy';
  } else {
    return 'cloudy';
  }
}

/**
 * Extracts weather state from forecast data at a specific time index
 *
 * @param data - The forecast data containing all variables
 * @param timeIndex - The index in the time array to extract data from
 * @param time - The Date object for the time point (used for time of day calculation)
 * @param getLocalHour - Optional function to get the local hour in user's timezone
 * @returns Complete weather state or null if required data is missing
 */
export function extractWeatherState(
  data: Partial<Record<InternalVariable, number[]>>,
  timeIndex: number,
  time: Date,
  getLocalHour?: (date: Date) => number,
): WeatherState | null {
  // Extract required variables
  const temperature = data['air_temperature_at_height_level_2m']?.[timeIndex];
  const precipitation = data['precipitation_amount_sum_1h']?.[timeIndex];
  const lowCloud = data['cloud_area_fraction_at_entire_atmosphere_low_type']?.[timeIndex];
  const mediumCloud = data['cloud_area_fraction_at_entire_atmosphere_medium_type']?.[timeIndex];
  const highCloud = data['cloud_area_fraction_at_entire_atmosphere_high_type']?.[timeIndex];
  const humidity = data['relative_humidity_at_height_level_2m']?.[timeIndex];
  const windSpeed = data['wind_speed_at_height_level_10m']?.[timeIndex];

  // Temperature is required - if missing, we can't provide a weather state
  if (temperature === undefined) {
    return null;
  }

  // Calculate cloud cover (using 0 as default if individual layers are missing)
  const totalCloudCover = calculateTotalCloudCover(lowCloud ?? 0, mediumCloud ?? 0, highCloud ?? 0);

  // Determine weather condition
  const condition = determineWeatherCondition(precipitation ?? 0, totalCloudCover);

  // Determine time of day based on user's timezone
  // If a function to get local hour is provided, use it
  // Otherwise, fall back to UTC hours
  const hour = getLocalHour ? getLocalHour(time) : time.getUTCHours();
  const timeOfDay = determineTimeOfDay(hour);

  return {
    condition,
    timeOfDay,
    temperature: Math.round(temperature), // Round to nearest degree
    precipitation: precipitation ?? 0,
    cloudCover: totalCloudCover,
    humidity: humidity ?? 0,
    windSpeed: windSpeed ?? 0,
  };
}

/**
 * Gets a human-readable description of the weather condition
 *
 * @param condition - The weather condition
 * @returns A string description of the condition
 */
export function getConditionDescription(condition: WeatherCondition): string {
  const descriptions: Record<WeatherCondition, string> = {
    clear: 'Clear',
    mostly_clear: 'Mostly Clear',
    partly_cloudy: 'Partly Cloudy',
    mostly_cloudy: 'Mostly Cloudy',
    cloudy: 'Cloudy',
    light_rain: 'Light Rain',
    rain: 'Rain',
    heavy_rain: 'Heavy Rain',
  };
  return descriptions[condition];
}
